import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import StorageIcon from '@mui/icons-material/Storage'
import SpeedIcon from '@mui/icons-material/Speed'
import BugReportIcon from '@mui/icons-material/BugReport'
import {
  systemService,
  SystemStatus,
  DatabaseInfo,
  DatabaseConnectionInfo,
  DatabaseStats,
  SlowQueriesInfo,
  ErrorsInfo,
} from '@/services/systemService'

const SystemInfo = () => {
  const [status, setStatus] = useState<SystemStatus | null>(null)
  const [dbInfo, setDbInfo] = useState<DatabaseInfo | null>(null)
  const [dbConnection, setDbConnection] = useState<DatabaseConnectionInfo | null>(null)
  const [dbStats, setDbStats] = useState<DatabaseStats | null>(null)
  const [slowQueries, setSlowQueries] = useState<SlowQueriesInfo | null>(null)
  const [errors, setErrors] = useState<ErrorsInfo | null>(null)
  const [loading, setLoading] = useState(false)

  const refresh = async () => {
    setLoading(true)
    try {
      const [sys, db, dbConn, stats, queries, errs] = await Promise.all([
        systemService.fetchSystemStatus(),
        systemService.fetchDatabaseInfo().catch(() => null),
        systemService.fetchDatabaseConnection(),
        systemService.fetchDatabaseStats().catch(() => null),
        systemService.fetchSlowQueries().catch(() => null),
        systemService.fetchErrors().catch(() => null),
      ])
      setStatus(sys)
      setDbInfo(db)
      setDbConnection(dbConn)
      setDbStats(stats)
      setSlowQueries(queries)
      setErrors(errs)
    } catch (error) {
      console.error('Error fetching system info:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    const interval = setInterval(refresh, 30000)
    return () => clearInterval(interval)
  }, [])

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">System Information & Monitoring</Typography>
        <Button startIcon={<RefreshIcon />} variant="outlined" onClick={refresh} disabled={loading}>
          Refresh Now
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* System Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Status
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Last checked: {status ? new Date(status.checkedAt).toLocaleString() : '—'}
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Chip
                  label={`Database: ${status?.database === 'up' ? 'Online' : 'Down'}`}
                  color={status?.database === 'up' ? 'success' : 'error'}
                  icon={status?.database === 'up' ? <CheckCircleIcon /> : <ErrorIcon />}
                />
                <Chip
                  label={`Status: ${status?.status ?? 'unknown'}`}
                  color={status?.status === 'ready' ? 'success' : status?.status === 'degraded' ? 'warning' : 'error'}
                />
                <Typography variant="body2">
                  <strong>Response Time:</strong> {status?.responseTimeMs ?? 0} ms
                </Typography>
                <Typography variant="body2">
                  <strong>Version:</strong> {status?.version ?? '—'}
                </Typography>
                <Typography variant="body2">
                  <strong>Environment:</strong> {status?.environment ?? '—'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Database Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Database Info
              </Typography>
              {dbInfo ? (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Alembic Version:</strong> {dbInfo.alembicVersion || '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Deposits:</strong> {(dbInfo.totalDeposits ?? 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Withdrawals:</strong> {(dbInfo.totalWithdrawals ?? 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total Users:</strong> {(dbInfo.totalUsers ?? 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Last Updated:</strong> {dbInfo.lastUpdated ? new Date(dbInfo.lastUpdated).toLocaleString() : '—'}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading…
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Database Statistics */}
        {dbStats && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <StorageIcon />
                  <Typography variant="h6">Database Statistics</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Last checked: {new Date(dbStats.checkedAt).toLocaleString()}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={3}>
                  {/* Database Size */}
                  {dbStats.databaseSize && (
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Database Size
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {dbStats.databaseSize}
                        </Typography>
                        {dbStats.databaseSizeBytes && (
                          <Typography variant="caption" color="text.secondary">
                            {formatBytes(dbStats.databaseSizeBytes)}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}

                  {/* Cache Hit Ratio */}
                  {dbStats.cacheHitRatio !== undefined && (
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Cache Hit Ratio
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6" color={dbStats.cacheHitRatio > 95 ? 'success.main' : 'warning.main'}>
                            {dbStats.cacheHitRatio}%
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={dbStats.cacheHitRatio}
                            sx={{ flex: 1, height: 8, borderRadius: 1 }}
                            color={dbStats.cacheHitRatio > 95 ? 'success' : 'warning'}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {dbStats.cacheHitRatio > 95 ? 'Excellent' : dbStats.cacheHitRatio > 90 ? 'Good' : 'Needs attention'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}

                  {/* Connections */}
                  {dbStats.connections && (
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Database Connections
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {dbStats.connections.active} / {dbStats.connections.max}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Active: {dbStats.connections.active} | Idle: {dbStats.connections.idle} | Max: {dbStats.connections.max}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(dbStats.connections.active / dbStats.connections.max) * 100}
                          sx={{ mt: 1, height: 8, borderRadius: 1 }}
                          color={dbStats.connections.active / dbStats.connections.max > 0.8 ? 'warning' : 'primary'}
                        />
                      </Box>
                    </Grid>
                  )}

                  {/* Locks */}
                  {dbStats.locks && (
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Database Locks
                        </Typography>
                        <Typography variant="h6" color={dbStats.locks.waitingLocks > 0 ? 'error.main' : 'success.main'}>
                          {dbStats.locks.waitingLocks} waiting
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Relation: {dbStats.locks.relationLocks} | Tuple: {dbStats.locks.tupleLocks}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* Table Sizes */}
                {dbStats.tables && dbStats.tables.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Table Sizes
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Table Name</TableCell>
                            <TableCell align="right">Total Size</TableCell>
                            <TableCell align="right">Table Size</TableCell>
                            <TableCell align="right">Indexes Size</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dbStats.tables.map((table) => (
                            <TableRow key={table.name}>
                              <TableCell>{table.name}</TableCell>
                              <TableCell align="right">
                                <strong>{table.totalSize}</strong>
                              </TableCell>
                              <TableCell align="right">{table.tableSize}</TableCell>
                              <TableCell align="right">{table.indexesSize}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {dbStats.databaseSizeError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    Error fetching database size: {dbStats.databaseSizeError}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Slow Queries */}
        {slowQueries && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SpeedIcon />
                  <Typography variant="h6">Slow Queries</Typography>
                </Box>
                {slowQueries.available ? (
                  <>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Last checked: {new Date(slowQueries.checkedAt).toLocaleString()}
                    </Typography>
                    {slowQueries.queries.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Query</TableCell>
                              <TableCell align="right">Calls</TableCell>
                              <TableCell align="right">Mean Time (ms)</TableCell>
                              <TableCell align="right">Max Time (ms)</TableCell>
                              <TableCell align="right">Total Time (ms)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {slowQueries.queries.map((query, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  <Tooltip title={query.query}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        maxWidth: 400,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {query.query}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="right">{query.calls.toLocaleString()}</TableCell>
                                <TableCell align="right">
                                  <Chip
                                    label={`${query.meanExecTime.toFixed(2)} ms`}
                                    size="small"
                                    color={query.meanExecTime > 1000 ? 'error' : query.meanExecTime > 100 ? 'warning' : 'default'}
                                  />
                                </TableCell>
                                <TableCell align="right">{query.maxExecTime.toFixed(2)} ms</TableCell>
                                <TableCell align="right">{query.totalExecTime.toFixed(2)} ms</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        No slow queries found. All queries are performing well!
                      </Alert>
                    )}
                  </>
                ) : (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {slowQueries.message || slowQueries.error || 'pg_stat_statements extension is not available'}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recent Errors */}
        {errors && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <BugReportIcon />
                  <Typography variant="h6">Recent Errors & Exceptions</Typography>
                  {errors.total > 0 && (
                    <Chip label={errors.total} color="error" size="small" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Last checked: {new Date(errors.checkedAt).toLocaleString()}
                </Typography>
                {errors.errors.length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    {errors.errors.slice(0, 10).map((error, idx) => (
                      <Accordion key={idx} sx={{ mb: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <ErrorIcon color="error" fontSize="small" />
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              <strong>{error.type}</strong>: {error.message}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(error.timestamp).toLocaleString()}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {error.context && Object.keys(error.context).length > 0 && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Context:
                                </Typography>
                                <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                  {JSON.stringify(error.context, null, 2)}
                                </Typography>
                              </Box>
                            )}
                            {error.traceback && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Traceback:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  component="pre"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                    backgroundColor: 'grey.100',
                                    p: 1,
                                    borderRadius: 1,
                                    overflow: 'auto',
                                    maxHeight: 300,
                                  }}
                                >
                                  {error.traceback}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    No errors found. System is running smoothly!
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Database Connection Details */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Database Connection Details
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Last checked: {dbConnection ? new Date(dbConnection.checkedAt).toLocaleString() : '—'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              {dbConnection ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Connection Status */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Connection Status
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {dbConnection.connection.status === 'connected' ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                      <Chip
                        label={dbConnection.connection.status.toUpperCase()}
                        color={dbConnection.connection.status === 'connected' ? 'success' : 'error'}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Response Time: {dbConnection.connection.responseTimeMs} ms
                      </Typography>
                    </Box>
                    {dbConnection.connection.error && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {dbConnection.connection.error}
                      </Alert>
                    )}
                  </Box>

                  {/* Connection Details */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Connection Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Host
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {dbConnection.connection.host}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Port
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {dbConnection.connection.port}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Database
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {dbConnection.connection.database}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          User
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {dbConnection.connection.user}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider />

                  {/* Data Reading Status */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Data Reading Status
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      {dbConnection.data.canRead ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <ErrorIcon color="error" />
                      )}
                      <Chip
                        label={dbConnection.data.canRead ? 'CAN READ DATA' : 'CANNOT READ DATA'}
                        color={dbConnection.data.canRead ? 'success' : 'error'}
                        size="small"
                      />
                    </Box>
                    {dbConnection.data.error && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {dbConnection.data.error}
                      </Alert>
                    )}
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Total Deposits
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {(dbConnection.data?.totalDeposits ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Total Withdrawals
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {(dbConnection.data?.totalWithdrawals ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Total Channels
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {(dbConnection.data?.totalChannels ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Total Statuses
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {(dbConnection.data?.totalStatuses ?? 0).toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider />

                  {/* Migration Info */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Migration Version
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {dbConnection.migration.alembicVersion || 'Not set'}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading connection details…
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default SystemInfo
